import { OrganizationalLogoOutSchema } from '../components/OrganizationalLogo/types';
import { UploadedFileSchema } from './CodeOfConductSurvey/types';

export enum ReportingPeriod {
  YEARLY = 'Y',
  MONTHLY = 'M',
  QUARTERLY = 'Q',
}

export enum GOODS_AND_SERVICES_OR_CAPITAL_GOODS {
  // eslint-disable-next-line no-unused-vars
  GOODS_AND_SERVICES = 'goods-and-services',
  // eslint-disable-next-line no-unused-vars
  CAPITAL_GOODS = 'capital-goods',
}

export interface GhgSupplierSurveyAnswerInSchema {
  company_name: string;
  vat_identifier: string;
  contact_email: string;
  has_sedex_questionnaire: boolean | null;
  wants_to_use_platform: boolean | null;
  zc_code: string | null;
  saq_file: UploadedFileSchema | null;
}

export interface GhgSupplierSurveyAnswerBaseSchema extends GhgSupplierSurveyAnswerInSchema {
  id: number;
  is_submitted: boolean;
  submitted_at: string | null;
  questionnaire_answer_esg_submitted_at: string | null;
  questionnaire_answer_environment_submitted_at: string | null;
  questionnaire_answer_esg: string;
  questionnaire_answer_environment: string;
  questionnaire_answer_esg_submitted: boolean;
  questionnaire_answer_environment_submitted: boolean;
  vat_identifier: string;
}

export interface GhgSupplierSurveyAnswerSchema extends GhgSupplierSurveyAnswerBaseSchema {
  goods: GhgSupplierGoodsBase[];
}

export interface GhgSupplierSurveyAnswerWithTokenSchema extends GhgSupplierSurveyAnswerSchema {
  qv3_token_questionnaire_esg: string | null;
  qv3_token_questionnaire_environment: string | null;
}

export interface GhgSupplierSurveyBase {
  id: number;
  created_at: string;
  co2_organization: number;
  product_name: string;
  supplier_name: string;
  vat_identifier: string;
  reporting_period: string;
  access_token: string | null;
}

export interface GhgSupplierSurvey extends GhgSupplierSurveyBase {
  answer: GhgSupplierSurveyAnswerSchema | null;
  organization_id: number | null;
  branches?: string[];
}

export interface GhgSupplierSurveySummary {
  organization_id: number | null;
  vat_identifier: string;
  reporting_period: string;
  registered_at: string | null;
  progress_percentage: number | null;
  esg_questionnaire_submitted_at: string | null;
  esg_questionnaire_type: string | null;
  environment_questionnaire_submitted_at: string | null;
  scope_1_entries: number;
  scope_2_entries: number;
  branches: string[];
}

export interface GhgSupplierGoodsBase {
  id: number;
  amount: number;
  reporting_period: string;
  type: GOODS_AND_SERVICES_OR_CAPITAL_GOODS;
  emission_kg_eq_co2_per_unit: number;
  co2_organization_id: number;
  survey_answer_id: number;
}

export interface GhgSupplierGoods extends GhgSupplierGoodsBase {
  survey_answer: GhgSupplierSurveyAnswerBaseSchema;
  survey: GhgSupplierSurveyBase;
}

export interface GhgSupplierSurveyWithTokenSchema extends Omit<
  GhgSupplierSurvey,
  'vat_identifier'
> {
  answer: GhgSupplierSurveyAnswerWithTokenSchema | null;
  organization_name: string;
  organization_logo: OrganizationalLogoOutSchema | null;
  is_registered: boolean;
  prefill_company_name?: string | null;
  prefill_contact_email?: string | null;
}

export interface GhgSupplierSurveySchema {
  organization_name: string;
  organization_logo: OrganizationalLogoOutSchema | null;
  // Where a registration through this Raben link lands, resolved by the
  // backend from the link: the environment (e.g. "Raben", or a demo copy such
  // as "Raben NL demo") and the link's branch, translated, with its country
  // (null while the branch has none).
  environment_name: string;
  branch_name: string | null;
  branch_country_name: string | null;
}

export interface GhgSupplierSurveyGetTokenSchema {
  token: string;
}

export type SupplierQuestionnaireTypes =
  'SupplierQuestionnaireEsg' | 'SupplierQuestionnaireEnvironment';

export interface RegistrationRabenTokenSchema {
  token: string;
  branch_name: string;
}

export interface GhgDataEntryCompletedStatisticsSchema {
  scope_1_percent: number;
  scope_2_percent: number;
  scope_3_percent: number;
}

// C16 -- backend/ghg_suppliers/api/api_consent.py CommitmentSnapshotSchema.
// The immutable facts captured at Accept time (BR-1): `null` until the
// first accept, and never re-derived from the live organization afterwards
// (EC-4 -- a later rename must not change what a regenerated document
// says). Always read the company name / VAT shown on the generated PDF
// from THIS object, never from `useGhgOrganization()`.
export interface RabenConsentSnapshotSchema {
  company_name: string;
  vat_prefixed: string;
  accepted_on: string;
}

// C15 -- backend/ghg_suppliers/api/api_consent.py RabenConsentOutSchema.
// `window_active` / `commitment_pilot_enabled` are the two server-computed
// gates the pop-ups read (DECISIONS-RESOLVED "C15 §2"): gating stays
// backend-only, the FE just reads these fields off this one response
// instead of a hardcoded const. `accepted_at`/`declined_at` are an additive
// split of `consent_at` -- pop-up gating must key off `consent` alone, see
// the comment on RabenConsentOutSchema. `snapshot` is C16's addition, see
// RabenConsentSnapshotSchema above. RG 28.08 (D2/D3) additions are optional
// so older mocks/responses still type-check: `is_direct_member` is true only
// for the carrier's own users with write access (the only ones who may
// decide -- everyone else gets 403 on POST, and any 403 redirects to /403,
// so render decision buttons only when it is strictly `true`);
// `decline_reason*` describe a CURRENT decline; `decline_reason_options` is
// the ordered list of codes for the Letter's decline form.
export interface RabenConsentSchema {
  consent: boolean | null;
  consent_at: string | null;
  accepted_at: string | null;
  declined_at: string | null;
  window_active: boolean;
  commitment_pilot_enabled: boolean;
  snapshot: RabenConsentSnapshotSchema | null;
  is_direct_member?: boolean;
  decline_reason?: string | null;
  decline_reason_text?: string | null;
  decline_reason_options?: string[];
}

// RG 28.08 D3: what DeclineReasonForm hands to the decline mutations.
// `reason` is a code from the server-provided options (null = "I prefer not
// to say"); `reasonText` is only meaningful for the 'other' code.
export interface DeclinePayload {
  reason: string | null;
  reasonText: string;
}

// C17 -- backend/ghg_suppliers/api/api_commitment_target.py
// CommitmentTargetOutSchema. Every field here is either server-computed
// (BR-1: target_year/reduction_pct/target_emissions are derived from
// base_year alone, never from anything the client sends) or frozen at set
// time (BR-3, mirroring RabenConsentSnapshotSchema above) -- render these
// values as-is, never re-derive them client-side.
export interface CommitmentTargetRowSchema {
  base_year: number;
  target_year: number;
  reduction_pct: number;
  base_emissions: number;
  target_emissions: number;
  company_name: string;
  vat_prefixed: string;
  created_at: string;
  // RG 28.08 9.1: POST answers with these two (accepted_at = created_at,
  // content_version = the Statement text version it was accepted under).
  accepted_at?: string;
  content_version?: string | null;
}

// RG 28.08 8.4, D3: a recorded refusal to set the target.
export interface CommitmentTargetDeclineSchema {
  declined_at: string;
  reason: string | null;
  reason_text: string | null;
}

// GET .../commitment-target's shape: the active target (or null before the
// first set, Goal 1's "not-set" state) plus the base years currently
// eligible to pick from (BR-2 -- already narrowed server-side to scope 1+2
// complete AND within the published reduction table, EC-1). RG 28.08:
// `declined` is set only while there is no target and the carrier declined;
// `decline_reason_options` are the codes for the target's decline form.
export interface CommitmentTargetStateSchema {
  target: CommitmentTargetRowSchema | null;
  eligible_years: number[];
  declined?: CommitmentTargetDeclineSchema | null;
  decline_reason_options?: string[];
}

// RG 28.08 8.4/9.1, D5: GET .../commitment-target/preview?base_year= -- the
// figures the Statement shows for a base year BEFORE accepting, computed by
// the same server code as the POST (writes nothing). Render as-is.
export interface CommitmentTargetPreviewSchema {
  base_year: number;
  target_year: number;
  reduction_pct: number;
  base_emissions: number;
  target_emissions: number;
  company_name: string;
  vat_prefixed: string;
  statement_date: string;
}

// RG 28.08 2.3: GET .../commitment-target/readiness -- one row per FINISHED
// base year the reduction table covers, with the full-year scope 1/2
// completion and whether Scope 1 holds data, which together decide
// eligibility. Works before the Letter is accepted (`consent` says where the
// Letter stands).
export interface CommitmentReadinessYearSchema {
  year: number;
  scope_1_percent: number;
  scope_2_percent: number;
  // Raben live review 2026-10-02; absent from older servers.
  scope_1_has_data?: boolean;
  eligible: boolean;
}

export interface CommitmentReadinessSchema {
  consent: boolean | null;
  target_set: boolean;
  years: CommitmentReadinessYearSchema[];
  eligible_years: number[];
}
